/**
 * chatPreferences slice — user-tunable chat client settings that persist
 * across reloads via localStorage.
 *
 * Currently houses:
 *   - soundOnSend     — play a blip when sending a message
 *   - soundOnReceive  — play a chime when receiving a message from someone else
 *
 * localStorage is read once at slice load (initial state) and written on
 * every toggle. Reducers are not "pure" in the strict sense — they have a
 * side effect of persisting — but the state result is deterministic and
 * the slice is small, so the trade-off is intentional.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

const STORAGE_KEY = 'gc.chatPrefs.v1';

export interface ChatPreferencesState {
  soundOnSend: boolean;
  soundOnReceive: boolean;
}

const defaults: ChatPreferencesState = {
  soundOnSend: true,
  soundOnReceive: true,
};

function loadInitial(): ChatPreferencesState {
  if (typeof window === 'undefined') return defaults;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    return {
      soundOnSend:
        typeof parsed.soundOnSend === 'boolean'
          ? parsed.soundOnSend
          : defaults.soundOnSend,
      soundOnReceive:
        typeof parsed.soundOnReceive === 'boolean'
          ? parsed.soundOnReceive
          : defaults.soundOnReceive,
    };
  } catch {
    return defaults;
  }
}

function persist(state: ChatPreferencesState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota or private mode — ignore */
  }
}

const slice = createSlice({
  name: 'chatPreferences',
  initialState: loadInitial(),
  reducers: {
    setSoundOnSend(state, action: PayloadAction<boolean>) {
      state.soundOnSend = action.payload;
      persist(state);
    },
    setSoundOnReceive(state, action: PayloadAction<boolean>) {
      state.soundOnReceive = action.payload;
      persist(state);
    },
  },
});

export const { setSoundOnSend, setSoundOnReceive } = slice.actions;
export default slice.reducer;

/* ------------------------------------------------------------------ */
/* Selectors                                                          */
/* ------------------------------------------------------------------ */

type RootSliceShape = { chatPreferences: ChatPreferencesState };

export const selectSoundOnSend = (s: RootSliceShape): boolean =>
  s.chatPreferences.soundOnSend;

export const selectSoundOnReceive = (s: RootSliceShape): boolean =>
  s.chatPreferences.soundOnReceive;
